// 审批流程服务类

import { 
  ApprovalApplicationExtended, 
  ApprovalFlow, 
  ApprovalNode, 
  ApprovalLevel, 
  ApprovalResult, 
  ApprovalStatus,
  UserRole,
  User,
  Notification
} from '../types/approval';
import { parseMoneyToCents } from '../utils/money';

export class ApprovalFlowService {
  // 计算申请的最大单价和总金额
  static calculateAmounts(application: ApprovalApplicationExtended): { maxUnitPrice: number; totalAmount: number } {
    let maxUnitPriceCents = 0;
    let totalCents = 0;

    // 计算耗材申请
    if (application.consumableProjects) {
      application.consumableProjects.forEach(project => {
        const pc = parseMoneyToCents(project.price || '0');
        const amount = parseFloat(String(project.amount || '0').trim());
        if (Number.isFinite(amount) && amount > 0 && pc >= 0) {
          maxUnitPriceCents = Math.max(maxUnitPriceCents, pc);
          totalCents += Math.round(pc * amount);
        }
      });
    }

    // 计算试剂申请
    if (application.reagentProjects) {
      application.reagentProjects.forEach(project => {
        const pc = parseMoneyToCents(project.price || '0');
        const amount = parseFloat(String(project.amount || '0').trim());
        if (Number.isFinite(amount) && amount > 0 && pc >= 0) {
          maxUnitPriceCents = Math.max(maxUnitPriceCents, pc);
          totalCents += Math.round(pc * amount);
        }
      });
    }

    // 计算测试申请
    if (application.testProjects) {
      application.testProjects.forEach(project => {
        const pc = parseMoneyToCents(project.unitPrice || '0');
        const amount = parseFloat(String(project.quantity || '0').trim());
        maxUnitPriceCents = Math.max(maxUnitPriceCents, pc);
        if (Number.isFinite(amount) && amount > 0) {
          totalCents += Math.round(pc * amount);
        }
      });
    }

    return { maxUnitPrice: maxUnitPriceCents / 100, totalAmount: totalCents / 100 };
  }

  // 创建审批流程
  static createApprovalFlow(application: ApprovalApplicationExtended, applicant: User): ApprovalFlow {
    const { maxUnitPrice, totalAmount } = this.calculateAmounts(application);
    
    // 更新申请的最大单价和总金额
    application.maxUnitPrice = maxUnitPrice;
    application.totalAmount = totalAmount;

    // 创建审批节点
    const nodes: ApprovalNode[] = [
      {
        level: '一级审批',
        approver: applicant.supervisorId || '导师',
        approverId: applicant.supervisorId || '',
        approverRole: '导师',
        result: '待审批',
        createdAt: new Date().toISOString()
      }
    ];

    // 根据金额决定二级审批人
    const secondApproverRole = maxUnitPrice >= 100 ? '管理员' : 
      (application.category === '耗材申请' ? '耗材管理员' : '试剂管理员');
    
    nodes.push({
      level: '二级审批',
      approver: secondApproverRole,
      approverId: '',
      approverRole: secondApproverRole as UserRole,
      result: '待审批',
      createdAt: new Date().toISOString()
    });

    // 三级审批人
    const thirdApproverRole = application.category === '耗材申请' ? '耗材管理员' : '试剂管理员';
    nodes.push({
      level: '三级审批',
      approver: thirdApproverRole,
      approverId: '',
      approverRole: thirdApproverRole as UserRole,
      result: '待审批',
      createdAt: new Date().toISOString()
    });

    return {
      id: `flow_${Date.now()}`,
      applicationId: application.key,
      status: '一级审批中',
      currentLevel: '一级审批',
      nodes,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  }

  // 处理审批
  static processApproval(
    flow: ApprovalFlow, 
    approverId: string, 
    result: ApprovalResult, 
    comment?: string
  ): ApprovalFlow {
    const updatedFlow = { ...flow };
    const currentNode = updatedFlow.nodes.find(node => 
      node.level === flow.currentLevel && node.result === '待审批'
    );

    if (!currentNode) {
      throw new Error('找不到当前审批节点');
    }

    // 更新当前节点
    currentNode.approverId = approverId;
    currentNode.result = result;
    currentNode.comment = comment;
    currentNode.approvedAt = new Date().toISOString();

    // 确定下一个状态
    if (result === '驳回') {
      updatedFlow.status = '已驳回';
    } else if (result === '通过') {
      // 检查是否还有下一级审批
      const nextLevel = this.getNextApprovalLevel(flow.currentLevel);
      if (nextLevel) {
        updatedFlow.currentLevel = nextLevel;
        updatedFlow.status = `${nextLevel}中` as ApprovalStatus;
      } else {
        updatedFlow.status = '已通过';
      }
    }

    updatedFlow.updatedAt = new Date().toISOString();
    return updatedFlow;
  }

  // 获取下一个审批级别
  private static getNextApprovalLevel(currentLevel: ApprovalLevel): ApprovalLevel | null {
    switch (currentLevel) {
      case '一级审批':
        return '二级审批';
      case '二级审批':
        return '三级审批';
      case '三级审批':
        return null;
      default:
        return null;
    }
  }

  // 检查用户是否有审批权限
  static canApprove(user: User, flow: ApprovalFlow, application: ApprovalApplicationExtended): boolean {
    const currentNode = flow.nodes.find(node => 
      node.level === flow.currentLevel && node.result === '待审批'
    );

    if (!currentNode) {
      return false;
    }

    // 检查角色权限
    if (user.role === currentNode.approverRole) {
      return true;
    }

    // 特殊检查：导师审批
    if (currentNode.approverRole === '导师' && currentNode.approverId === user.id) {
      return true;
    }

    return false;
  }

  // 获取待审批的申请列表
  static getPendingApprovals(user: User, applications: ApprovalApplicationExtended[]): ApprovalApplicationExtended[] {
    return applications.filter(app => {
      if (!app.approvalFlow) return false;
      return this.canApprove(user, app.approvalFlow, app);
    });
  }

  // 创建通知
  static createNotification(
    userId: string,
    title: string,
    content: string,
    type: 'approval' | 'system' | 'reminder',
    relatedId?: string
  ): Notification {
    return {
      id: `notif_${Date.now()}`,
      userId,
      title,
      content,
      type,
      isRead: false,
      relatedId,
      createdAt: new Date().toISOString()
    };
  }

  // 发送审批通知
  static sendApprovalNotification(
    approver: User,
    application: ApprovalApplicationExtended,
    flow: ApprovalFlow
  ): Notification {
    const title = `新的${application.category}需要审批`;
    const content = `申请人：${application.applicant}\n申请标题：${application.title}\n金额：¥${application.totalAmount?.toFixed(2) || '0.00'}`;
    
    return this.createNotification(
      approver.id,
      title,
      content,
      'approval',
      application.key
    );
  }
}
